import { useMemo } from 'react';
import { deviceDetect, deviceType, getUA, isDesktop, isEmbedded, isMobile } from 'react-device-detect';

type TBaseEngineDevice = {
  engineName: string;
  engineVersion: string;
  osName: string;
  osVersion: string;
  userAgent: string;
};

type TBaseVendorDevice = {
  model: string;
  os: string;
  osVersion: string;
  ua: string;
  vendor: string;
};

type TMobileDevice = {
  isMobile: true;
} & TBaseVendorDevice;

type TTabletDevice = {
  isTablet: true;
} & TBaseVendorDevice;

type TDesktopDevice = {
  isBrowser: true;
  browserMajorVersion: string;
  browserFullVersion: string;
  browserName: string;
} & TBaseEngineDevice;

type TEmbeddedDevice = {
  isEmbedded: true;
  vendor: string;
  model: string;
} & TBaseEngineDevice;

type TSmartTVDevice = {
  isSmartTV: true;
} & TBaseEngineDevice;

type TConsoleDevice = {
  isConsole: true;
} & TBaseEngineDevice;

type TWearableDevice = {
  isWearable: true;
} & TBaseEngineDevice;

type TDeviceType = 'browser' | 'mobile' | 'tablet' | 'embedded' | 'smarttv' | 'console' | 'wearable';

type TDevice = {
  platformOSVersion?: string;
  platform: string;
  model: string;
  userAgent: string;
  type: TDeviceType;
};

type TDetectedDevice =
  | TMobileDevice
  | TTabletDevice
  | TDesktopDevice
  | TEmbeddedDevice
  | TWearableDevice
  | TConsoleDevice
  | TSmartTVDevice;

const useNativeDevice = (): TDevice => {
  const device = useMemo(() => {
    const detectedDevice = deviceDetect(getUA) as TDetectedDevice;

    let baseDevice: Partial<TDevice> = {
      platformOSVersion: detectedDevice.osVersion !== 'none' ? detectedDevice.osVersion : undefined,
      type: deviceType as TDeviceType,
    };

    switch (true) {
      case isMobile:
        {
          const { os, model, ua } = detectedDevice as TMobileDevice | TTabletDevice;
          baseDevice = {
            ...baseDevice,
            platform: os,
            model,
            userAgent: ua,
          };
        }
        break;
      case isDesktop:
        {
          const { osName, userAgent, browserName } = detectedDevice as TDesktopDevice;
          baseDevice = {
            ...baseDevice,
            platform: osName,
            model: browserName,
            userAgent,
          };
        }
        break;
      case isEmbedded:
        {
          const { osName, userAgent, model } = detectedDevice as TEmbeddedDevice;
          baseDevice = {
            ...baseDevice,
            platform: osName,
            model,
            userAgent,
          };
        }
        break;
      default:
        {
          const { osName, userAgent, engineName } = detectedDevice as TSmartTVDevice | TConsoleDevice | TWearableDevice;
          baseDevice = {
            ...baseDevice,
            platform: osName,
            model: engineName,
            userAgent,
          };
        }
        break;
    }
    return baseDevice as TDevice;
  }, []);

  return device;
};

export default useNativeDevice;
export type { TDevice, TDeviceType };
